/**
 * Date range picker.
 *
 * Built from headlessui Popover + date-fns + Tailwind rather than pulling in another calendar
 * library: both dependencies are already in the app, and this keeps the control looking like
 * the rest of the form components instead of shipping a third-party stylesheet.
 *
 * Behaviour: first click sets the start, second sets the end (dates are swapped if picked in
 * reverse). Hovering previews the range. Presets cover the common cases so most users never
 * touch the calendar.
 *
 * Value shape is { from: Date|null, to: Date|null } — plain Dates, no wrapper objects.
 */
import React from "react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
} from "date-fns";

import { classNames } from "../../helpers";

const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

const buildMonthGrid = (month) =>
  eachDayOfInterval({
    start: startOfWeek(startOfMonth(month), { weekStartsOn: 1 }),
    end: endOfWeek(endOfMonth(month), { weekStartsOn: 1 }),
  });

const PRESETS = [
  { label: "Today", get: () => ({ from: startOfDay(new Date()), to: startOfDay(new Date()) }) },
  { label: "Last 7 days", get: () => ({ from: startOfDay(subDays(new Date(), 6)), to: startOfDay(new Date()) }) },
  { label: "Last 30 days", get: () => ({ from: startOfDay(subDays(new Date(), 29)), to: startOfDay(new Date()) }) },
  { label: "This month", get: () => ({ from: startOfMonth(new Date()), to: startOfDay(new Date()) }) },
];

function MonthGrid({ month, from, to, hovered, onPick, onHover }) {
  const days = buildMonthGrid(month);

  // While only a start is chosen, preview the range against whatever is hovered.
  const previewEnd = to || hovered;
  const inRange = (day) =>
    from && previewEnd && !isSameDay(from, previewEnd) &&
    isAfter(day, from) && isBefore(day, previewEnd);

  return (
    <div className="w-64">
      <p className="text-sm font-medium text-center mb-2">{format(month, "MMMM yyyy")}</p>

      <div className="grid grid-cols-7 mb-1">
        {WEEKDAYS.map((d) => (
          <span key={d} className="text-[11px] text-gray-400 text-center py-1">{d}</span>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {days.map((day) => {
          const outside = !isSameMonth(day, month);
          const isStart = from && isSameDay(day, from);
          const isEnd = to && isSameDay(day, to);
          const selected = isStart || isEnd;
          const between = inRange(day);

          return (
            <button
              key={day.toISOString()}
              type="button"
              onClick={() => onPick(day)}
              onMouseEnter={() => onHover(day)}
              className={classNames(
                "h-8 text-sm flex items-center justify-center",
                outside ? "text-gray-300" : "text-gray-800",
                between ? "bg-gray-100" : "",
                isStart ? "rounded-l-full" : "",
                isEnd ? "rounded-r-full" : "",
                selected ? "bg-primary text-white font-medium rounded-full" : "hover:bg-gray-100 rounded-full"
              )}
            >
              {format(day, "d")}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function TwDateRangePicker({
  label = "",
  value = { from: null, to: null },
  onChange = () => {},
  placeholder = "All dates",
  className = "",
}) {
  const { from, to } = value || {};
  const [viewMonth, setViewMonth] = React.useState(startOfMonth(from || new Date()));
  const [hovered, setHovered] = React.useState(null);

  const handlePick = (day) => {
    const picked = startOfDay(day);

    // No start yet, or a complete range already exists → begin a new range.
    if (!from || (from && to)) {
      onChange({ from: picked, to: null });
      return;
    }
    // Second click. Picking before the start reverses the range rather than rejecting it.
    if (isBefore(picked, from)) {
      onChange({ from: picked, to: from });
    } else {
      onChange({ from, to: picked });
    }
    setHovered(null);
  };

  const summary = from
    ? to
      ? `${format(from, "d MMM yyyy")} – ${format(to, "d MMM yyyy")}`
      : `${format(from, "d MMM yyyy")} – …`
    : "";

  return (
    <div className={classNames("w-full", className)}>
      {label ? (
        <label className="block text-sm/6 font-medium text-black/[0.6]">{label}</label>
      ) : null}

      <Popover className="relative mt-2">
        <PopoverButton className="flex w-full items-center gap-2 rounded-2xl bg-white px-4 md:px-5 py-3 md:py-4 text-left outline outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-bz">
          <svg viewBox="0 0 20 20" fill="currentColor" className="size-5 shrink-0 text-gray-400" aria-hidden="true">
            <path d="M5.75 2a.75.75 0 01.75.75V4h7V2.75a.75.75 0 011.5 0V4h.25A2.75 2.75 0 0118 6.75v8.5A2.75 2.75 0 0115.25 18H4.75A2.75 2.75 0 012 15.25v-8.5A2.75 2.75 0 014.75 4H5V2.75A.75.75 0 015.75 2zM3.5 8v7.25c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25V8h-13z" />
          </svg>

          <span className={classNames("flex-1 text-base sm:text-sm/6 truncate", summary ? "text-gray-900" : "text-gray-400")}>
            {summary || placeholder}
          </span>

          {from ? (
            <span
              role="button"
              aria-label="Clear dates"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange({ from: null, to: null });
              }}
              className="shrink-0 text-gray-400 hover:text-gray-700 text-sm px-1"
            >
              ✕
            </span>
          ) : null}
        </PopoverButton>

        <PopoverPanel className="absolute right-0 z-30 mt-2 rounded-xl border border-gray-200 bg-white p-4 shadow-lg">
          <div className="flex flex-wrap gap-2 mb-3 pb-3 border-b border-gray-100">
            {PRESETS.map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => {
                  const next = preset.get();
                  onChange(next);
                  setViewMonth(startOfMonth(next.from));
                }}
                className="rounded-full border border-gray-300 px-3 py-1 text-xs text-gray-700 hover:bg-gray-50"
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              aria-label="Previous month"
              onClick={() => setViewMonth(addMonths(viewMonth, -1))}
              className="rounded p-1 text-gray-500 hover:bg-gray-100"
            >
              ‹
            </button>
            <button
              type="button"
              aria-label="Next month"
              onClick={() => setViewMonth(addMonths(viewMonth, 1))}
              className="rounded p-1 text-gray-500 hover:bg-gray-100"
            >
              ›
            </button>
          </div>

          <div className="flex gap-6" onMouseLeave={() => setHovered(null)}>
            <MonthGrid
              month={viewMonth}
              from={from}
              to={to}
              hovered={hovered}
              onPick={handlePick}
              onHover={setHovered}
            />
            <div className="hidden sm:block">
              <MonthGrid
                month={addMonths(viewMonth, 1)}
                from={from}
                to={to}
                hovered={hovered}
                onPick={handlePick}
                onHover={setHovered}
              />
            </div>
          </div>
        </PopoverPanel>
      </Popover>
    </div>
  );
}
